import { flexRender } from '@tanstack/react-table'
import React from 'react'
import { observer } from 'mobx-react-lite'
import { TableBodyProps } from './types'
import { tableBody } from './config'
import { useStore } from '~/app/store'
import { tableHead } from '../table-head/config'

export const TableBody = observer(
  <T,>({
    variantRow,
    className,
    data,
    onRowClick,
    hasSubRowContent,
    subRowComponent,
    isSubRowOpen,
    cellsCount,
    alignBodyStyle,
    colorizeCell,
    textSize,
    disableWhitespaceWrap,
    allowZeroStockClick = false,
    enableRowHoverTracking = true,
    getRowUrl,
    getRowClassName,
  }: TableBodyProps<T>) => {
    const { ordersStore } = useStore()
    const hasData = data?.length > 0

    const SubRowComponent = subRowComponent
    return (
      <tbody
        className={`${tableHead({
          className,
        })} ${alignBodyStyle}`}
      >
        {!hasData && (
          <tr>
            <td
              colSpan={cellsCount}
              className="text-center text-[#707070] text-[12px] py-4 h-[250px] bg-white"
            >
              Нет данных для отображения
            </td>
          </tr>
        )}
        {hasData &&
          data.map((row, index) => {
            const copy = { ...row.original } as {
              demand?: boolean
              id: string
              stock: number
              categoryId?: null | string
              checked?: boolean
              state?: string
            }

            if (!row?.original) return null
            const typedRow = copy

            const theNumberOfNecessaryGoods =
              row.original && 'variationId' in copy
            const shouldRenderSubRow = isSubRowOpen?.(row.original) ?? true
            const rowID = typedRow.id
            const cells = row.getVisibleCells()

            let bgColor = 'bg-white'

            const rowClassName = getRowClassName?.(row.original)

            if (typedRow?.demand === true) {
              bgColor = 'bg-[#FEF2F2]'
            } else if (typedRow?.checked === true) {
              bgColor = 'bg-blue-100'
            }

            const resolvedRowClassName = rowClassName || bgColor

            const hasCustomHover = rowClassName?.includes('hover:bg-')
            const hover =
              typedRow?.checked === true || hasCustomHover
                ? ''
                : 'hover:bg-slate-100'
            const isRowInteractive = Boolean(onRowClick || getRowUrl)
            let rowCursorClass = 'cursor-default'

            if (typedRow?.stock === 0 && !allowZeroStockClick) {
              rowCursorClass = 'opacity-50 cursor-not-allowed'
            } else if (isRowInteractive) {
              rowCursorClass = 'cursor-pointer'
            }
            return (
              <React.Fragment key={row.id}>
                <tr
                  onMouseEnter={() => {
                    if (enableRowHoverTracking) {
                      ordersStore.setTempID(rowID)
                    }
                  }}
                  onMouseLeave={() => {
                    if (enableRowHoverTracking) {
                      ordersStore.setTempID('')
                    }
                  }}
                  onClick={(
                    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                  ) => {
                    if (allowZeroStockClick || typedRow?.stock !== 0) {
                      onRowClick?.(row.original, e)
                    }
                  }}
                  onMouseDown={(e: React.MouseEvent<HTMLTableRowElement>) => {
                    if (e.button === 1) {
                      e.preventDefault()
                      e.stopPropagation()
                    }
                  }}
                  onMouseUp={(e: React.MouseEvent<HTMLTableRowElement>) => {
                    if (e.button === 1) {
                      e.preventDefault()
                      e.stopPropagation()

                      if (typedRow?.stock !== 0 && getRowUrl) {
                        const url = getRowUrl(row.original)
                        window.open(url, '_blank', 'noopener,noreferrer')
                      }
                    }
                  }}
                  className={`group ${tableBody({
                    variantRow,
                  })} ${hover} transition-all ${resolvedRowClassName} ${rowCursorClass}  ${
                    index === data.length - 1
                      ? 'border-none'
                      : 'border-t border-gray-200'
                  } `}
                >
                  {cells.map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                        className={`${tableBody({
                          variantRow,
                        })} ${theNumberOfNecessaryGoods ? 'text-left' : ''} ${
                          colorizeCell && colorizeCell(cell)
                        }
                         ${textSize} text-gray-800
                          ${!disableWhitespaceWrap ? 'whitespace-nowrap' : ''}
                      `}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    )
                  })}
                </tr>

                {hasSubRowContent && SubRowComponent && shouldRenderSubRow ? (
                  <tr className="bg-white">
                    <td colSpan={cells.length}>
                      <SubRowComponent
                        state={typedRow.state}
                        id={rowID}
                        path={
                          typedRow.categoryId === null ? 'product' : 'category'
                        }
                      />
                    </td>
                  </tr>
                ) : null}

                {index !== data.length - 1 && (
                  <tr className={`${'border-t border-gray-200'}`} />
                )}
              </React.Fragment>
            )
          })}
      </tbody>
    )
  },
)
